import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { useI18n } from "../../i18n/I18nProvider";
import { extractError } from "../../lib/errors";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { EmptyState } from "../../components/EmptyState";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { RefreshCw, Plus, Save } from "lucide-react";

/**
 * AuditFollowupTab — ISO 19011 §6.7 Follow-up.
 * S16b-3 C13: adds "New action" modal (POST) and inline-edit of status/notes (PATCH).
 */

const STATUS_OPTIONS = ["pending", "in_progress", "verified", "closed"];

const AuditFollowupTab = ({ audit }) => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    description: "",
    responsible: "",
    due_date: "",
    status: "pending",
    notes: "",
  });
  // D-S17-004: selected non-conformity id — required for ISO 19011 §6.7 linkage
  const [selectedNCId, setSelectedNCId] = useState(null);

  // Inline-edit state keyed by followup_id
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});

  const fetchActions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audits/${audit.audit_id}/followup-actions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActions(res.data);
    } catch (e) {
      console.error("Failed to fetch follow-up actions", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFindings = async () => {
    try {
      const res = await axios.get(`${API}/audits/${audit.audit_id}/findings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Only non-conformities can anchor follow-up actions
      setFindings(res.data.filter((f) => f.finding_type === "nonconformity"));
    } catch {
      setFindings([]);
    }
  };

  useEffect(() => {
    fetchActions();
    fetchFindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit.audit_id]);

  const openModal = () => {
    setForm({ description: "", responsible: "", due_date: "", status: "pending", notes: "" });
    // D-S17-004: reset NC selection on each modal open
    setSelectedNCId(null);
    setShowModal(true);
  };

  const handleCreate = async () => {
    if (!form.description.trim()) {
      // D-S17-012: use composite i18n key instead of string concatenation
      toast.error(t("audits.followup.errors.description_required"));
      return;
    }
    // D-S17-004: ISO 19011 §6.7 — a follow-up action must be linked to an NC
    if (!selectedNCId) {
      toast.error(t("audits.followup.nc_required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        description: form.description.trim(),
        responsible: form.responsible.trim() || null,
        due_date: form.due_date || null,
        status: form.status,
        notes: form.notes.trim() || null,
        // D-S17-004: send selected NC — array of 1 element per ISO 19011 §6.7
        linked_nc_ids: [selectedNCId],
      };
      await axios.post(`${API}/audits/${audit.audit_id}/followup-actions`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("audits.followup.created"));
      setShowModal(false);
      await fetchActions();
    } catch (e) {
      toast.error(t("audits.followup.create_failed") + ": " + extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (action) => {
    setEditing((prev) => ({
      ...prev,
      [action.followup_id]: {
        status: action.status || "pending",
        notes: action.notes || "",
      },
    }));
  };

  const cancelEdit = (followupId) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[followupId];
      return next;
    });
  };

  const saveEdit = async (followupId) => {
    const edits = editing[followupId];
    if (!edits) return;
    setSaving((prev) => ({ ...prev, [followupId]: true }));
    try {
      await axios.patch(
        `${API}/audits/${audit.audit_id}/followup-actions/${followupId}`,
        edits,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("audits.followup.updated"));
      cancelEdit(followupId);
      await fetchActions();
    } catch (e) {
      toast.error(t("audits.followup.update_failed") + ": " + extractError(e));
    } finally {
      setSaving((prev) => {
        const next = { ...prev };
        delete next[followupId];
        return next;
      });
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">{t("loading")}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header row with "New action" button */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {t("audits.followup.none") && actions.length === 0 ? t("audits.followup.none") : ""}
        </span>
        <Button size="sm" variant="outline" onClick={openModal} className="flex items-center gap-1">
          <Plus className="h-4 w-4" />
          {t("audits.followup.new")}
        </Button>
      </div>

      {actions.length === 0 ? (
        <EmptyState
          icon={RefreshCw}
          title={t("audits.followup.none")}
          description={t("audits.followup.effectiveness")}
        />
      ) : (
        <div className="space-y-3">
          {actions.map((action) => {
            const isEditing = !!editing[action.followup_id];
            const editState = editing[action.followup_id] || {};
            const isSaving = !!saving[action.followup_id];

            return (
              <Card key={action.followup_id} className="border-border">
                <CardContent className="p-3 space-y-2">
                  {/* Top row: status badge + review date */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {isEditing ? (
                      <select
                        className="text-xs border rounded px-2 py-1 bg-background"
                        value={editState.status}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [action.followup_id]: { ...prev[action.followup_id], status: e.target.value },
                          }))
                        }
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {t(`audits.followup.status.${s}`)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Badge variant="secondary" className="text-xs capitalize">
                        {t(`audits.followup.status.${action.status}`) || action.status}
                      </Badge>
                    )}
                    {action.next_review_date && (
                      <span className="text-xs text-muted-foreground">
                        {t("audits.followup.next_review")}: {new Date(action.next_review_date).toLocaleDateString()}
                      </span>
                    )}
                    {action.due_date && (
                      <span className="text-xs text-muted-foreground">
                        {t("audits.followup.fields.due_date")}: {new Date(action.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {action.description && (
                    <p className="text-sm font-medium">{action.description}</p>
                  )}

                  {/* Responsible */}
                  {action.responsible && (
                    <p className="text-xs text-muted-foreground">
                      {t("audits.followup.fields.responsible")}: {action.responsible}
                    </p>
                  )}

                  {/* Effectiveness review */}
                  {action.effectiveness_review && (
                    <p className="text-sm">{action.effectiveness_review}</p>
                  )}

                  {/* Notes — inline-editable */}
                  {isEditing ? (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">
                        {t("audits.followup.fields.notes")}
                      </label>
                      <Input
                        value={editState.notes}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [action.followup_id]: { ...prev[action.followup_id], notes: e.target.value },
                          }))
                        }
                        className="text-sm"
                      />
                    </div>
                  ) : (
                    action.notes && (
                      <p className="text-xs text-muted-foreground italic">{action.notes}</p>
                    )
                  )}

                  {/* Linked NCs — D-S17-011: show NC title instead of raw id */}
                  {action.linked_nc_ids?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {action.linked_nc_ids.map((id) => {
                        const nc = findings.find((f) => f.finding_id === id);
                        const label = nc
                          ? (nc.title || (nc.description || "").slice(0, 40) + ((nc.description || "").length > 40 ? "…" : ""))
                          : id;
                        return (
                          <Badge
                            key={id}
                            variant={nc ? "outline" : "secondary"}
                            className="text-xs"
                            title={id}
                          >
                            {label}
                          </Badge>
                        );
                      })}
                    </div>
                  )}

                  {/* Edit / Save / Cancel controls */}
                  <div className="flex gap-2 pt-1">
                    {isEditing ? (
                      <>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => saveEdit(action.followup_id)}
                          disabled={isSaving}
                          className="flex items-center gap-1"
                        >
                          <Save className="h-3 w-3" />
                          {t("audits.followup.save")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => cancelEdit(action.followup_id)}
                          disabled={isSaving}
                        >
                          {t("audits.followup.cancel")}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(action)}
                        className="text-xs"
                      >
                        {t("edit")}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New action modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("audits.followup.new")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {/* D-S17-004: Non-conformity selector — ISO 19011 §6.7 requires linkage */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t("audits.followup.fields.non_conformity")} *
              </label>
              {findings.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2 px-3 border rounded bg-muted/30">
                  {t("audits.followup.no_nonconformities")}
                </p>
              ) : (
                <select
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                  value={selectedNCId ?? ""}
                  onChange={(e) => setSelectedNCId(e.target.value || null)}
                >
                  <option value="">{t("audits.followup.fields.non_conformity_placeholder")}</option>
                  {findings.map((f) => {
                    const label = f.title
                      ? f.title
                      : (f.description || "").slice(0, 80) + ((f.description || "").length > 80 ? "…" : "");
                    const meta = [f.category, f.severity].filter(Boolean).join(" · ");
                    return (
                      <option key={f.finding_id} value={f.finding_id}>
                        {label}{meta ? ` — ${meta}` : ""}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* Description — required */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t("audits.followup.fields.description")} *
              </label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                className="text-sm"
              />
            </div>

            {/* Responsible */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t("audits.followup.fields.responsible")}
              </label>
              <Input
                value={form.responsible}
                onChange={(e) => setForm((f) => ({ ...f, responsible: e.target.value }))}
                className="text-sm"
              />
            </div>

            {/* Due date */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t("audits.followup.fields.due_date")}
              </label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                className="text-sm"
              />
            </div>

            {/* Status */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t("audits.followup.fields.status")}
              </label>
              <select
                className="w-full border rounded px-3 py-2 text-sm bg-background"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`audits.followup.status.${s}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t("audits.followup.fields.notes")}
              </label>
              <Input
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowModal(false)} disabled={submitting}>
              {t("audits.followup.cancel")}
            </Button>
            {/* D-S17-004: disable Create when no NCs exist — can't link without one */}
            <Button onClick={handleCreate} disabled={submitting || findings.length === 0}>
              {submitting ? t("loading") : t("audits.followup.new")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditFollowupTab;
